/**
 * Which portal this bundle is serving.
 *
 * One codebase, two front doors. Either deployment style works:
 *  - one build served from two hostnames -> set REACT_APP_BRAND_PORTAL_HOSTS
 *  - a dedicated brand build             -> set REACT_APP_PORTAL=brand
 *
 * REACT_APP_PORTAL wins when both are set, so a dedicated build never depends
 * on what hostname it happens to be served from.
 *
 * This only decides which route tree and shell render. It is NOT a security
 * boundary — the backend scopes every brand query from the auth context.
 */
export type PortalType = "admin" | "brand";

export const BRAND_ROLE_NAME = "Brand";

const brandHosts = (process.env.REACT_APP_BRAND_PORTAL_HOSTS ?? "")
  .split(",")
  .map((host) => host.trim().toLowerCase())
  .filter(Boolean);

export const getPortalType = (): PortalType => {
  const explicit = (process.env.REACT_APP_PORTAL ?? "").trim().toLowerCase();
  if (explicit === "brand") return "brand";
  if (explicit === "admin") return "admin";

  if (typeof window === "undefined") return "admin";

  return brandHosts.includes(window.location.hostname.toLowerCase())
    ? "brand"
    : "admin";
};

export const isBrandPortal = (): boolean => getPortalType() === "brand";

export const isBrandRole = (role?: string | null): boolean =>
  (role ?? "").trim().toLowerCase() === BRAND_ROLE_NAME.toLowerCase();

/**
 * The signed-in user's role, read straight from the persisted profile.
 *
 * Deliberately not a hook: the portal decision is made above the router, and a
 * brand must not render the admin tree even for the first frame while a hook
 * hydrates from storage.
 */
export const getStoredRole = (): string | null => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem("profile");
    return raw ? ((JSON.parse(raw)?.role as string) ?? null) : null;
  } catch {
    return null;
  }
};

/**
 * Brand accounts get the brand route tree on ANY hostname.
 *
 * The host mapping alone is not enough. A brand holds the ordinary
 * `creatives.view` permission — the very permission the tenant creative pages
 * gate on — so signing in on the admin host would otherwise drop them into the
 * admin shell with upload and delete controls on screen. Their reads would
 * still come back scoped to their own rows, but the shell is not theirs.
 */
export const shouldRenderBrandPortal = (): boolean =>
  isBrandPortal() || isBrandRole(getStoredRole());

/**
 * Where to land after a successful login. Brand accounts have no dashboard
 * permission, so sending them to /dashboard would bounce them to /unauthorized.
 */
export const getPostLoginRoute = (role?: string | null): string =>
  isBrandRole(role) ? "/creatives" : "/dashboard";
